import React from 'react'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import { findElement } from '../../../shared/domain/flows/element.no-deps'
import type { ElementId } from '../../../shared/domain/flows/types.no-deps'
import type { SyncGuide } from '../../../shared/domain/guides/types.no-deps'
import { omit } from '../../../shared/lib/helpers'
import { GuideBottomSheet } from '../guide/GuideBottomSheet'
import type { AppFileGuideDetails, AppGuide } from '../guide/Model'
import { ExecutionPage, type Props, type Report } from './ExecutionPage'

interface WorkflowGuideExecutionPage extends Omit<Props, 'sectionId'> {
    guideSectionId?: ElementId
    showSection: (sectionId: ElementId) => void
    workflowGuideReport: Report
}

function WorkflowGuideExecutionPage(props: WorkflowGuideExecutionPage) {
    // We want to pass down the props to the execution page as they are, excluding the ones below.
    const executionPageProps = omit(props, 'footer', 'reportId', 'routeToExecutionPage', 'showHeader', 'startTimestamp')

    return (
        <ExecutionPage
            {...executionPageProps}
            footer={null}
            loopPathIds={props.loopPathIds}
            reportId={props.workflowGuideReport.id}
            routeToExecutionPage={(_, opts) => {
                if (opts.sectionId) {
                    props.showSection(opts.sectionId)
                }
            }}
            sectionId={props.guideSectionId || props.workflowGuideReport.flow.id}
            showHeader={false}
            startTimestamp={null}
        />
    )
}

interface GuideSheetState {
    isOpen: boolean
    guideToExecute?: AppGuide
    workflowGuideReport?: Report
    sectionStack: ElementId[]
    syncGuides: SyncGuide[]
    title?: string
}

const defaultGuideSheetState: GuideSheetState = {
    isOpen: false,
    sectionStack: [],
    syncGuides: [],
}

function getSectionLabel(sectionId: ElementId, report?: Report): string | null {
    if (!report) {
        return null
    }

    const sectionElement = findElement(report.flow, sectionId)
    if (!sectionElement) {
        return null
    }

    return sectionElement.label
}

/**
 * A component that presents an flow execution page, as well as a bottom/side sheet for linked guides to steps.
 *
 * Internally, the component manages the state related to the guides sheet.
 *
 * @param props component props
 * @returns React component
 */
export function ExecutionPageWithGuides(props: Omit<Props, 'onLinkedGuidesToStep'>) {
    const [guideSheetState, setGuideSheetState] = React.useState<GuideSheetState>(defaultGuideSheetState)

    // Pushes given section ID to the guide section stack.
    const pushGuideSectionStack = async (sectionId: ElementId) => {
        const nextSheetState = { ...guideSheetState, sectionStack: [...guideSheetState.sectionStack, sectionId] }

        // Set the section element label as the sheet title.
        const sectionLabel = getSectionLabel(sectionId, guideSheetState.workflowGuideReport)
        if (sectionLabel) {
            nextSheetState.title = sectionLabel
        }

        setGuideSheetState(nextSheetState)
    }

    // Pops the last section ID from the guide section stack.
    const popGuideSectionStack = () => {
        const currentSectionStack = guideSheetState.sectionStack
        const nextSectionStack = currentSectionStack.slice(0, currentSectionStack.length - 1)
        const nextSheetState = { ...guideSheetState, sectionStack: nextSectionStack }

        // If not more sections, remove title from sheet state.
        if (nextSectionStack.length === 0) {
            setGuideSheetState(omit(nextSheetState, 'title'))
            return
        }

        const sectionId = nextSectionStack[nextSectionStack.length - 1]
        const sectionLabel = getSectionLabel(sectionId, guideSheetState.workflowGuideReport)
        // If for some reason we can't determine the section label, remove tile from sheet state.
        if (!sectionLabel) {
            setGuideSheetState(omit(nextSheetState, 'title'))
            return
        }

        nextSheetState.title = sectionLabel
        setGuideSheetState(nextSheetState)
    }

    const onClickExecuteGuide = async (guideToExecute: AppGuide) => {
        if (guideToExecute.variant === 'workflow') {
            // If the guide to be executed is a workflow guide, then we also want to keep track of the report,
            // which we might need to get any given section's title.
            const workflowGuideReport = await props.fetchGuideReportId(guideToExecute).then(props.fetchReport)

            setGuideSheetState({ ...guideSheetState, guideToExecute, workflowGuideReport })
            return
        }

        setGuideSheetState({ ...guideSheetState, guideToExecute })
    }

    const onLinkedGuidesToStep = (syncGuides: SyncGuide[]) =>
        setGuideSheetState({ ...defaultGuideSheetState, isOpen: true, syncGuides })

    const onGuideSheetBack = () =>
        guideSheetState.sectionStack.length > 0
            ? popGuideSectionStack()
            : setGuideSheetState(omit(guideSheetState, 'guideToExecute', 'title'))

    const onGuideSheetClose = () => setGuideSheetState(defaultGuideSheetState)

    const fetchFileGuideDetails = (fileId: FileId): Promise<AppFileGuideDetails> =>
        props.fetchFileGuideDetailsMap([fileId]).then((urlMap) => urlMap.get(fileId))

    const guideSectionId = guideSheetState.sectionStack[guideSheetState.sectionStack.length - 1]

    return (
        <div className="AppCore__flow__ExecutionPageWithGuides">
            <ExecutionPage {...props} onLinkedGuidesToStep={onLinkedGuidesToStep}></ExecutionPage>
            <GuideBottomSheet
                deps={props.deps}
                allowImageZoom={props.allowImageZoom}
                fetchFileGuideDetails={fetchFileGuideDetails}
                fetchGuideParentInfo={props.fetchGuideParentInfo}
                fetchReportGuideDetails={props.fetchReportGuideDetails}
                fetchAppGuides={props.fetchAppGuides}
                guideToExecute={guideSheetState.guideToExecute}
                onClickExecuteGuide={onClickExecuteGuide}
                onClickGuideParentInfo={props.onClickGuideParentInfo}
                onBack={onGuideSheetBack}
                onClose={onGuideSheetClose}
                onOpenFile={props.onOpenFile}
                open={guideSheetState.isOpen}
                renderCachedImage={props.renderCachedImage}
                renderWorkflowGuideExecution={() => {
                    const workflowGuideReport = guideSheetState.workflowGuideReport

                    // This case shouldn't happen - it's just for good practice since
                    // `guideSheetState.workflowGuideReport` is optional.
                    if (!workflowGuideReport) {
                        throw new Error('Expected a workflow guide report by this point')
                    }

                    return (
                        <WorkflowGuideExecutionPage
                            {...props}
                            guideSectionId={guideSectionId}
                            onLinkedGuidesToStep={onLinkedGuidesToStep}
                            showSection={pushGuideSectionStack}
                            workflowGuideReport={workflowGuideReport}
                        />
                    )
                }}
                syncGuides={guideSheetState.syncGuides}
                title={guideSheetState.title}
            />
        </div>
    )
}
